'use client';

import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, ChevronDown, CheckCircle, Send } from 'lucide-react';

const serviceOptions = [
  'Batch Payment System',
  'Ceremony & Contribution Management',
  'Digital Wallet Solutions',
  'Other',
];

const faqs = [
  {
    question: 'How quickly can we get started with your services?',
    answer:
      'Most organizations are up and running within 2-4 weeks. Our onboarding team handles configuration, data migration, and staff training to ensure a smooth transition.',
  },
  {
    question: 'Do you support mobile money integrations?',
    answer:
      'Yes. Our batch payment and digital wallet solutions integrate with major mobile money providers across East Africa, including M-Pesa, Tigo Pesa, Airtel Money, and more.',
  },
  {
    question: 'Is there a minimum organization size to use your platform?',
    answer:
      'We serve organizations of all sizes — from small community groups to large enterprises with thousands of members. Our pricing scales with your needs.',
  },
  {
    question: 'What kind of support do you offer?',
    answer:
      'We provide dedicated account management, 24/7 technical support for critical issues, and regular check-ins during your first 90 days to ensure success.',
  },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <main className="pt-16">
        {/* Hero */}
        <section
          className="relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 50%, #4CAF50 100%)' }}
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/20 -translate-y-1/2 translate-x-1/3" />
            <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-white/10 translate-y-1/2 -translate-x-1/4" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
              Get in Touch
            </h1>
            <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
              Have questions about our solutions? We&apos;d love to hear from you. Reach out and our team will respond within 24 hours.
            </p>
          </div>
        </section>

        {/* Quick Contact Cards */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
            {[
              {
                icon: Mail,
                title: 'Email Us',
                lines: ['info@meristem.co.tz', 'support@meristem.co.tz'],
              },
              {
                icon: Phone,
                title: 'Call Us',
                lines: ['+255 743 552 403', 'Mon - Fri, 8AM - 6PM EAT'],
              },
              {
                icon: MapPin,
                title: 'Visit Us',
                lines: ['Dodoma', 'Tanzania'],
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="bg-white rounded-xl shadow-[0_4px_20px_rgba(27,94,32,0.08)] p-6 flex items-start gap-4 hover:shadow-[0_8px_30px_rgba(27,94,32,0.12)] transition-shadow duration-300"
                >
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #1B5E20, #4CAF50)' }}
                  >
                    <Icon size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-1">{card.title}</h3>
                    {card.lines.map((line) => (
                      <p key={line} className="text-sm text-gray-600 leading-relaxed">
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Form + Map */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(27,94,32,0.08)] p-8 lg:p-10">
                <div className="mb-8">
                  <h2
                    className="text-2xl font-bold text-gray-900 mb-3"
                    style={{ color: '#1B5E20' }}
                  >
                    Send us a message
                  </h2>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Have a question or interested in our solutions? Fill out the form below and our team will get back to you within 24 hours.
                  </p>
                </div>

                {submitted ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                    <CheckCircle size={48} className="mx-auto mb-4" style={{ color: '#4CAF50' }} />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Thank you for reaching out. Our team will review your message and respond within 24 hours.
                    </p>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: '', email: '', company: '', phone: '', service: '', message: '' });
                      }}
                      className="text-sm font-semibold transition-colors duration-200 hover:underline"
                      style={{ color: '#1B5E20' }}
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent focus:bg-white transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@company.com"
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent focus:bg-white transition-all duration-200"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Company / Organization
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Acme Corp"
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent focus:bg-white transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+255 xxx xxx xxx"
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent focus:bg-white transition-all duration-200"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Service Interest
                      </label>
                      <div className="relative">
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:border-transparent focus:bg-white transition-all duration-200"
                        >
                          <option value="">Select a service...</option>
                          {serviceOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                        <ChevronDown
                          size={16}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={6}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project, requirements, or any questions you have..."
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent focus:bg-white transition-all duration-200 resize-none"
                      />
                    </div>

                    <div className="pt-2">
                      <button
                        type="submit"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg text-sm font-semibold text-white transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                        style={{
                          background: 'linear-gradient(135deg, #1B5E20, #4CAF50)',
                        }}
                      >
                        <Send size={16} />
                        Send Message
                      </button>
                      <p className="text-xs text-gray-500 mt-4">
                        We respect your privacy. Your information will not be shared with third parties.
                      </p>
                    </div>
                  </form>
                )}
              </div>
            </div>

            {/* Map / Location */}
            <div className="lg:col-span-2">
              <h2
                className="text-2xl font-bold text-gray-900 mb-2"
                style={{ color: '#1B5E20' }}
              >
                Our Office
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Visit us at our Dodoma headquarters.
              </p>

              <div className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-[0_4px_20px_rgba(27,94,32,0.06)]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3982.123456789!2d35.7468!3d-6.1630!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMDknNDYuOCJTIDM1wrA0NCc0OC41IkU!5e0!3m2!1sen!2stz!4v1234567890"
                  width="100%"
                  height="208"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-t-xl"
                ></iframe>
                <div className="p-5 space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#4CAF50' }} />
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Dodoma, Tanzania
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#4CAF50' }} />
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Office Hours</p>
                      <p className="text-sm text-gray-600">Monday - Friday, 8:00 AM - 6:00 PM (EAT)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#4CAF50' }} />
                    <p className="text-sm text-gray-600">+255 743 552 403</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-16 lg:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2
                className="text-2xl sm:text-3xl font-bold mb-3"
                style={{ color: '#1B5E20' }}
              >
                Frequently Asked Questions
              </h2>
              <p className="text-sm text-gray-500">
                Quick answers to common questions about our services.
              </p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-xl overflow-hidden transition-shadow duration-200 hover:shadow-[0_2px_12px_rgba(27,94,32,0.06)]"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left"
                  >
                    <span className="text-sm font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </span>
                    <ChevronDown
                      size={18}
                      className={`flex-shrink-0 text-gray-400 transition-transform duration-200 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === index ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
